import { DataSource } from '@angular/cdk/collections'
import { HttpErrorResponse } from '@angular/common/http'
import { isResponse, PagedRestResponse, toData, toSuccess } from '@mmx/shared'
import { isArray, isError } from 'lodash'
import { filter, map, merge, Observable, scan, share, Subject, takeUntil, zip } from 'rxjs'

import { AdvancedTableApiService } from './advanced-table-api-service'

export interface MaybeNewRecord {
  id?: any
  newRecord?: boolean
}

export const advancedTableDatasourceFactory = <T extends MaybeNewRecord>(apiService: AdvancedTableApiService<T>) => {
  return new AdvancedTableDataSource<T, AdvancedTableApiService<T>>(apiService)
}

export class AdvancedTableDataSource<RowModel extends MaybeNewRecord, Service extends AdvancedTableApiService<RowModel>>
implements DataSource<RowModel>
{
  loadingSubject = new Subject<boolean>()
  loading$ = this.loadingSubject.asObservable()
  disconnected = new Subject<void>()
  bust$ = new Subject<boolean>()

  constructor(
    protected apiService: Service,
  ) { }

  // once total is known for a given filter/search, we save it until explicity set to true in a page navigation event; 
  // this avoids re-running PagingHelper's count() scan for every page change
  collectionTotal$ = this.apiService.collectionResponse$.pipe(
    filter(isResponse),
    map((response) => (response as PagedRestResponse<RowModel[]>).total),
    scan((lastTotal: number, total: number | undefined) => total ?? lastTotal, 0),
    share(),
  )

  collectionRecord$ = this.apiService.collectionResponse$.pipe(
    filter(isResponse),
    map(toData),
    share(),
  )

  paginatonParams$ = this.apiService.collectionResponse$.pipe(
    filter(isResponse),
    map(({ startAt }: PagedRestResponse<RowModel[]>) => ({ startAt })),
  )

  notPaged$ = zip(this.collectionRecord$, this.collectionTotal$).pipe(
    map(([data, total]) => data.length >= total),
    share(),
  )

  collectionError$ = this.apiService.collectionResponse$.pipe(
    filter<HttpErrorResponse>(isError),
  )

  updateRecord$ = this.apiService.updateResponse$.pipe(
    filter(isResponse),
    map(toData),
    share(),
  )

  // insert updated records in current collection after setting newRecord flag
  // this allows the UI to highlight new and updated row
  protected internalDataRow$: Observable<RowModel[]> = merge(this.collectionRecord$, this.updateRecord$).pipe(
    scan((acc: RowModel[], record: RowModel | RowModel[]) => {
      if (isArray(record)) {
        return record
      } else {
        const idx = acc.findIndex(rowItem => rowItem.id === record.id)
        record.newRecord = true
        if (idx > -1) {
          acc.splice(idx, 1, record)
        } else {
          acc.unshift(record)
        }
        return acc
      }
    }, []),
  )

  // this can be overriden in any child class to do additional processing
  // for example, advanced-table-deletion-datasource.ts overrides this to
  // remove deleted records from current collection
  protected dataRow$: Observable<RowModel[]> = this.internalDataRow$

  empty$ = this.dataRow$.pipe(
    map<RowModel[], boolean>(data => data?.length === 0),
  )

  updateError$ = this.apiService.updateResponse$.pipe(
    filter<HttpErrorResponse>(isError),
  )

  collectionSuccess$ = this.apiService.collectionResponse$.pipe(
    filter(isResponse),
    map(toSuccess),
  )

  connect(): Observable<RowModel[]> {
    this.apiService.bust$
      .pipe(takeUntil(this.disconnected))
      .subscribe(this.bust$)
    return this.dataRow$
  }

  disconnect(): void {
    this.disconnected.next()
  }
}
